<!-- h-full, not grow: the shared wrapper in program-frm has no min-h-0, so a grow-based chain
     cannot shrink and the whole page scrolls instead of each panel scrolling in its own frame.
     This is the pattern the Program User Groups panel already uses to fill the height.
     No card-body either — the two panels below carry their own borders. -->
<div *ngIf="visible" class="flex h-full min-h-0 flex-col gap-3">
  <p class="text-sm" style="color: var(--p-text-muted-color)">
    Move Account Users between this program's Program User Groups. Their allowance and issuance
    usage moves with them, so nobody starts again on a budget they have already spent.
  </p>

  <div *ngIf="loadError" class="rounded border border-red-300 bg-red-50 p-3 text-red-800">{{ loadError }}</div>

  <!-- One box, two states. While anything is ticked it says who would move — including anyone the
       filter is hiding, which is the only place that full set is visible. The moment a transfer
       lands the selection clears and the same box reports what happened; ticking again returns it
       to the pending state. Blue for pending, green for done: green on its own would say
       "finished" about something that has not happened yet. -->
  <div *ngIf="selectedCount > 0" class="rounded border border-blue-300 bg-blue-50 p-3 text-blue-900">
    {{ movingMessage }}
    <div class="mt-1 text-sm">Drop them on a Program User Group tile to move them.</div>
  </div>

  <div *ngIf="selectedCount === 0 && lastResultMessage"
    class="rounded border border-green-300 bg-green-50 p-3 text-green-900">
    {{ lastResultMessage }}
  </div>

  <div *ngIf="loading" class="flex items-center gap-2">
    <p-progressSpinner styleClass="h-6 w-6" strokeWidth="4" />
    <span>Loading…</span>
  </div>

  <div *ngIf="board?.DisqualifiedReason && !loading"
    class="rounded border border-amber-300 bg-amber-50 p-4 text-amber-900">
    {{ board!.DisqualifiedReason }}
  </div>

  <div *ngIf="board && !board.DisqualifiedReason && !loading" class="flex min-h-0 flex-1 gap-4">

    <!-- Roster. overflow-hidden keeps the rounded border from being overrun by the scroll area. -->
    <div class="flex w-[26rem] min-h-0 shrink-0 flex-col overflow-hidden rounded border border-gray-200">
      <div class="flex shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 p-2">
        <span class="font-semibold">Account Users</span>
        <span class="text-sm text-gray-600">
          {{ visibleUsers.length }} shown · {{ selectedCount }} selected
        </span>
      </div>

      <!-- Filtering is a view concern: it never clears a tick, so a selection built up across
           several filters survives. The count below says how many are ticked but out of sight,
           because those still move on a drop. -->
      <div class="flex shrink-0 flex-col gap-2 border-b border-gray-200 p-2 text-sm">
        <p-select [options]="pugFilterOptions" optionLabel="label" optionValue="value"
          [(ngModel)]="pugFilterId" [showClear]="true" [filter]="true" filterBy="label"
          [disabled]="transferring" placeholder="All Program User Groups" styleClass="w-full" />

        <div class="flex items-center gap-2">
          <p-checkbox [binary]="true" [ngModel]="allSelectableSelected" (onChange)="toggleSelectAll()"
            inputId="pugTransferSelectAll" [disabled]="transferring" />
          <label for="pugTransferSelectAll">Select all{{ pugFilterId ? ' shown' : '' }}</label>
          <span *ngIf="lockedCount > 0" class="ml-auto text-amber-700">{{ lockedCount }} locked</span>
        </div>

        <div *ngIf="hiddenSelectedCount > 0" class="text-amber-800">
          {{ hiddenSelectedCount }} selected {{ hiddenSelectedCount === 1 ? 'user is' : 'users are' }}
          hidden by this filter and will still move.
        </div>
      </div>

      <div class="roster min-h-0 flex-1">
        <!-- pDraggableDisabled is the directive's own switch; binding [attr.draggable] here
             instead would fight it, since the directive binds that attribute itself. -->
        <div *ngFor="let u of visibleUsers" class="roster-row flex items-start gap-2 p-2"
          [class.locked]="isLocked(u)" [class.selected]="isSelected(u)" pDraggable="pugTransferUsers"
          [pDraggableDisabled]="isLocked(u) || transferring" (onDragStart)="onDragStart(u)"
          (onDragEnd)="onRosterDragEnd()" (click)="toggleUser(u)">

          <!-- Stop the click here: without it the event bubbles to the row and the row's handler
               toggles a second time, so ticking the box would leave it unchanged. -->
          <p-checkbox [binary]="true" [ngModel]="isSelected(u)" [disabled]="isLocked(u) || transferring"
            (click)="$event.stopPropagation()" (onChange)="toggleUser(u)" />

          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <span class="truncate font-medium">{{ u.Name }}</span>
              <!-- Badges a user moved earlier in this sitting, so a long session does not lose
                   track of who has already been dealt with. -->
              <span *ngIf="hasMoved(u)"
                class="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">moved</span>
            </div>
            <div class="truncate text-xs text-gray-600">
              {{ u.CurrentPugName ?? 'No group governs this program' }}
            </div>
            <div *ngIf="u.LockedReason" class="mt-0.5 text-xs text-amber-800">{{ u.LockedReason }}</div>
          </div>
        </div>

        <div *ngIf="board.Users.length === 0" class="p-4 text-center text-gray-600">
          No Account Users reach this program.
        </div>
        <div *ngIf="board.Users.length > 0 && visibleUsers.length === 0" class="p-4 text-center text-gray-600">
          No Account Users match this filter.
        </div>
      </div>
    </div>

    <!-- Tiles. Its own framed, independently scrolling panel, so a long group list never pushes
         the roster or the footer off screen. -->
    <div class="flex min-h-0 flex-1 flex-col overflow-hidden rounded border border-gray-200">
      <div class="flex shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 p-2">
        <span class="font-semibold">Program User Groups</span>
        <span class="text-sm text-gray-600">{{ board.Tiles.length }} groups</span>
      </div>
      <div class="min-h-0 flex-1 overflow-y-auto p-3">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <div *ngFor="let tile of board.Tiles" class="qb-detail-card flex flex-col gap-2 p-4"
          [class.tile-drop-target]="dragOverTileId === tile.Id" [class.tile-disabled]="!canReceive(tile)"
          pDroppable="pugTransferUsers" (onDragEnter)="onDragEnter(tile)" (onDragLeave)="onDragLeave(tile)"
          (onDrop)="onDrop(tile)">

          <div class="flex items-start justify-between gap-2">
            <span class="font-semibold">{{ tile.Name }}</span>
            <span class="shrink-0 text-sm text-gray-600">{{ tile.MemberCount }} here</span>
          </div>

          <!-- What differs between groups on one program. A tile showing only a name would be
               asking the administrator to choose blind. -->
          <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <span class="text-gray-500">PO</span>
            <span>{{ tile.PurchaseOrder ?? '—' }}</span>

            <span class="text-gray-500">Allowance</span>
            <span>
              <ng-container *ngIf="tile.AllowanceName; else noAllowance">
                {{ tile.AllowanceName }} · {{ fmt(tile.AllowanceAmt) }}
              </ng-container>
              <ng-template #noAllowance>—</ng-template>
            </span>

            <span class="text-gray-500">Issuance</span>
            <span>{{ tile.IssuanceCount > 0 ? tile.IssuanceCount + ' limits' : '—' }}</span>

            <ng-container *ngIf="orderWindowText(tile)">
              <span class="text-gray-500">Window</span>
              <span>{{ orderWindowText(tile) }}</span>
            </ng-container>
          </div>

          <div *ngIf="tile.CannotReceiveReason"
            class="mt-1 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800">
            {{ tile.CannotReceiveReason }}
          </div>

          <!-- A warning does NOT stop the drop. It is shown on the tile so the administrator
               reads it before dragging, and repeated in the confirm for a multi-user move. -->
          <div *ngIf="tile.WarningReason && !tile.CannotReceiveReason"
            class="mt-1 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
            {{ tile.WarningReason }}
          </div>

          <div *ngIf="!tile.CannotReceiveReason" class="mt-1 text-xs text-gray-500">
            Drop {{ selectedCount > 0 ? selectedCount : 'selected' }}
            {{ selectedCount === 1 ? 'user' : 'users' }} here
          </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
